/**
 * W3 — "Who can be scheduled" (field test 2026-10-16, Wave 3).
 *
 * An ADDITIVE card on Team Management for managers who hold manage_work_roles.
 * It shows the read-only diagnostics of the canonical work-eligibility
 * predicate (who is schedulable today vs under the real-employee rule, and
 * why), and lets the shop make the AUTHORIZED, AUDITED decisions the server
 * offers: grant Technician / Detail Specialist to someone who has JOINED,
 * acknowledge that a roster-only person is not schedulable, and switch the
 * shop's gate between observe and enforce. It never invites, never creates an
 * account and never edits the full role set (that stays in the existing role
 * editor and invitation flow above it).
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  WORK_ELIGIBILITY_REASON_LABEL,
  acknowledgeTechnicianNotSchedulable,
  fetchWorkEligibilityReport,
  fetchWorkEligibilitySummary,
  isResolvableByWorkRoleDecision,
  reconcileTechnicianWorkRoles,
  setShopWorkEligibilityMode,
  type WorkEligibilityReportRow,
  type WorkEligibilitySummary,
} from '@/lib/roles/workEligibility';

interface Props {
  shopId: string;
}

function errorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return 'Something went wrong.';
}

export function WorkEligibilityReconciliationCard({ shopId }: Props) {
  const { toast } = useToast();
  const [rows, setRows] = useState<WorkEligibilityReportRow[] | null>(null);
  const [summary, setSummary] = useState<WorkEligibilitySummary | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([fetchWorkEligibilityReport(shopId), fetchWorkEligibilitySummary(shopId)]);
      setRows(r);
      setSummary(s);
    } catch (e) {
      // 42501 = this manager cannot manage work roles here: the card is not theirs to see.
      if ((e as { code?: string })?.code === '42501') setHidden(true);
      else setRows([]);
    }
  }, [shopId]);

  useEffect(() => { void load(); }, [load]);

  if (hidden || !rows || !summary) return null;

  const needsAttention = rows.filter((r) => r.employment_status === 'Active' && !r.operational);
  const enforced = summary.mode === 'enforce';
  const reasonText = reason.trim();

  const act = async (fn: () => Promise<unknown>, done: string) => {
    if (!reasonText) {
      toast({ title: 'A reason is required', description: 'Every scheduling decision is recorded with its reason.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await fn();
      toast({ title: done });
      await load();
    } catch (e) {
      toast({ title: 'Not changed', description: errorMessage(e), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="work-eligibility-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Who can be scheduled
          <Badge variant={enforced ? 'default' : 'secondary'} data-testid="work-eligibility-mode">
            {enforced ? 'Enforced' : 'Observe only'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Only people who have joined OptiBay for this shop and have a Technician or Detail Specialist work role can
          receive new work once enforced. {enforced
            ? 'This shop enforces it: roster-only names cannot be assigned or start timers. Existing assignments are kept.'
            : 'Today this is a report: nothing changes until you enforce it.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm" data-testid="work-eligibility-summary">
          Schedulable today: <strong>{summary.schedulable_legacy}</strong> · Under the rule: <strong>{summary.operational}</strong>
          {summary.would_lose > 0 && (
            <> · Would lose eligibility: <strong>{summary.would_lose}</strong>
              {summary.would_lose_with_open_work > 0 && <> ({summary.would_lose_with_open_work} with open work)</>}</>
          )}
        </p>

        {needsAttention.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Why not schedulable</TableHead>
                <TableHead>Open work</TableHead>
                <TableHead className="text-right">Decision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {needsAttention.map((r) => (
                <TableRow key={r.technician_id} data-testid="work-eligibility-row">
                  <TableCell>
                    {r.technician_name}
                    {r.duplicate_suspect && <Badge variant="outline" className="ml-2">possible duplicate</Badge>}
                  </TableCell>
                  <TableCell>
                    {WORK_ELIGIBILITY_REASON_LABEL[r.reason] ?? r.reason}
                    {r.proposed_work_roles && (
                      <span className="block text-xs text-muted-foreground">
                        Their record says: {r.proposed_work_roles.join(', ').toLowerCase()}
                      </span>
                    )}
                    {r.last_decision && (
                      <span className="block text-xs text-muted-foreground">Last decision: {r.last_decision.toLowerCase().replace(/_/g, ' ')}</span>
                    )}
                  </TableCell>
                  <TableCell>{r.open_assigned_tickets + r.live_timers}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {isResolvableByWorkRoleDecision(r.reason) && (
                      <>
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => reconcileTechnicianWorkRoles(r.technician_id, ['TECHNICIAN'], reasonText), 'Technician work role granted')}>
                          Technician
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => reconcileTechnicianWorkRoles(r.technician_id, ['DETAIL_SPECIALIST'], reasonText), 'Detail Specialist work role granted')}>
                          Detail Specialist
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" disabled={busy}
                      onClick={() => act(() => acknowledgeTechnicianNotSchedulable(r.technician_id, reasonText), 'Recorded as not schedulable')}>
                      Not schedulable
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="space-y-2">
          <Label htmlFor="work-eligibility-reason">Reason for the decision (recorded)</Label>
          <Input id="work-eligibility-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Confirmed with the shop owner on 2026-10-17" />
        </div>

        <div className="flex flex-wrap gap-2">
          {enforced ? (
            <Button variant="outline" disabled={busy}
              onClick={() => act(() => setShopWorkEligibilityMode(shopId, 'observe', reasonText), 'Back to observe only')}>
              Return to observe only
            </Button>
          ) : (
            <Button disabled={busy || summary.activation_blocked_empty_board} data-testid="work-eligibility-enforce"
              onClick={() => act(async () => {
                // Re-read the population the server will check, so the acknowledgement is exactly what is on screen now.
                const fresh = await fetchWorkEligibilitySummary(shopId);
                return setShopWorkEligibilityMode(shopId, 'enforce', reasonText, fresh.would_lose_ids);
              }, 'Real-employee scheduling enforced')}>
              Enforce for this shop
            </Button>
          )}
          {!enforced && summary.activation_blocked_empty_board && (
            <p className="text-sm text-destructive">
              Enforcing now would leave nobody schedulable. Grant work roles to people who have joined first.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
